import { animate, animateChild, query, style, transition, trigger } from '@angular/animations'
import { BreakpointObserver } from '@angular/cdk/layout'
import { NgClass, NgTemplateOutlet } from '@angular/common'
import { Component, computed, DestroyRef, effect, ElementRef, inject, input, Renderer2, signal, untracked } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { NotificationChannel } from '@app/modules/database/models/resend-confirmation-pricing'
import { buildInvitationInfo, InvitationInfo } from '@app/modules/invite/models/invitation-info.interface'
import { InviteService } from '@app/modules/invite/services/invite.service'
import { Persona } from '@app/v2/personas/types/persona.type'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { FilterButtonComponent } from '@app/v2/shared/components/filter-button/filter-button.component'
import { FilterDropdownTriggerComponent } from '@app/v2/shared/components/filter-dropdown-trigger/filter-dropdown-trigger.component'
import { FilterOptionComponent } from '@app/v2/shared/components/filter-option/filter-option.component'
import { ImportModalComponent } from '@app/v2/shared/components/import-modal/import-modal.component'
import { MobileAccordionComponent } from '@app/v2/shared/components/mobile-accordion/mobile-accordion.component'
import { MobileFilterDrawerComponent } from '@app/v2/shared/components/mobile-filter-drawer/mobile-filter-drawer.component'
import { NavbarService } from '@app/v2/shared/components/navbar/navbar.service'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { SearchFieldComponent } from '@app/v2/shared/components/search-field/search-field.component'
import { TruncationTooltipComponent } from '@app/v2/shared/components/truncation-tooltip/truncation-tooltip.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { MobileFilterDrawerService } from '@app/v2/shared/services/mobile-filter-drawer.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { QueuedJobsService } from '@app/v2/shared/services/queued-jobs.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { GenericTableSelectAllCollectionChange, GenericTableSelectionChange, GenericTableSort, GenericTableSortDirection } from '@app/v2/shared/types/generic-table.types'
import { ImportTargetColumn } from '@app/v2/shared/types/import-target-column.type'
import { buildInvitationCollectionDataMapping } from '@app/v2/shared/utils/csv-column-mapper.util'
import { buildImportTargetColumnsFromFormElements, pickPersonaFormElements } from '@app/v2/shared/utils/import-target-columns.util'
import { environment } from '@environments/environment'
import { NgxTippyService } from 'ngx-tippy-wrapper'
import { catchError, debounceTime, finalize, from, map, mergeAll, Observable, of, Subscription, toArray } from 'rxjs'
import { EventsListFlag } from '../../enums/event.enum'
import { InvitationTemplatesStep } from '../../enums/invitation-templates-step.enum'
import { PersonaSettingsFlag } from '../../enums/settings.enum'
import { EventService } from '../../services/event.service'
import { InvitationTrackerService } from '../../services/invitation-tracker.service'
import { SelfSettingsService } from '../../services/self-settings.service'
import { AddFromOtherEventModel } from '../../types/add-from-other-event.model'
import { AddFromOtherEventSelection } from '../../types/add-from-other-event.type'
import { ChannelTemplateModel } from '../../types/channel-template.model'
import { ChannelTemplate, EmailChannelTemplate, InvitationEmailChannelTemplateData, PatchChannelTemplatePayload, WhatsAppChannelTemplate } from '../../types/channel-template.type'
import { Event } from '../../types/event.type'
import { InvitationCollectionFilterModel } from '../../types/invitation-collection-filter.model'
import {
  InvitationCollectionFilterKey,
  InvitationCollectionFilterOption,
  InvitationCollectionFilters,
  InvitationCollectionSourceFilter
} from '../../types/invitation-collection-filter.type'
import { InvitationEmailTemplateModel } from '../../types/invitation-email-template.model'
import { InvitationEmailTemplate, InvitationEmailTemplateInfo, InvitationEmailTemplateTiming } from '../../types/invitation-email-template.type'
import {
  InvitationChannelHistoryState,
  InvitationCollectionBulkSendConfirmState,
  InvitationCollectionContext,
  InvitationCollectionDeleteItemsPreviewData,
  InvitationCollectionDeleteItemsRequest,
  InvitationCollectionDeleteItemsSelection,
  InvitationCollectionItem,
  InvitationCollectionItemSource,
  InvitationCollectionListResponse,
  InvitationCollectionSendBulkData,
  InvitationCollectionSendBulkRequest,
  InvitationCollectionSourceEvent,
  InvitationCollectionStatistics,
  InvitationCollectionSummary,
  InvitationTemplateChannel,
  InvitationTrackerFilterCardKey,
  InvitationTrackerPersona
} from '../../types/invitation-tracker.type'
import { WhatsAppInviteTemplateModel } from '../../types/whatsapp-invite-template.model'
import { WhatsAppInviteTemplateId } from '../../types/whatsapp-invite-template.type'
import { InvitationChannelBlockedSendState, resolveBlockedSendStateForItem } from '../../utils/invitation-channel-blocked-send.util'
import { getInvitationChannelLatestStatus, InvitationChannel } from '../../utils/invitation-channel-status.util'
import {
  INVITATION_CHANNEL_TITLE,
  INVITATION_CHANNEL_TO_API,
  INVITATION_CHANNEL_TO_NOTIFICATION,
  InvitationChannelSendRequest,
  invitationChannelUnitPrice
} from '../../utils/invitation-channel-ui.util'
import { getAddedEntriesCount, withDefaultAddResult } from '../../utils/invitation-collection-add.util'
import { buildBulkSendSummaryFromItems } from '../../utils/invitation-collection-bulk-send.util'
import {
  areAllSelectedInvitationCollectionItemsNonRemovable,
  buildInvitationCollectionDeleteItemsFilters,
  buildInvitationCollectionDeleteItemsSelectionPayload,
  canShowInvitationCollectionBulkRemove,
  createEmptyInvitationCollectionSelection,
  getInvitationCollectionSelectedKeys,
  getInvitationCollectionSelectionCount,
  getKnownSelectedInvitationCollectionItems,
  InvitationCollectionSelectionState,
  isAllInvitationCollectionSelected,
  isInvitationCollectionItemSelected,
  reconcileInvitationCollectionSelectionFromLoadedKeys,
  removeInvitationCollectionItemFromSelection,
  setInvitationCollectionSelectAll,
  toggleInvitationCollectionItemSelection
} from '../../utils/invitation-collection-selection.util'
import { EventsTimelineEmptyStateComponent } from '../events-timeline/components/events-timeline-empty-state/events-timeline-empty-state.component'
import { AddEntriesMethod, AddEntriesMethodModalComponent } from './components/add-entries-method-modal/add-entries-method-modal.component'
import { AddFromOtherEventModalComponent } from './components/add-from-other-event-modal/add-from-other-event-modal.component'
import { BulkDeleteModalComponent } from './components/bulk-delete-modal/bulk-delete-modal.component'
import { BulkSendConfirmModalComponent } from './components/bulk-send-confirm-modal/bulk-send-confirm-modal.component'
import { BulkSendSummaryModalComponent } from './components/bulk-send-summary-modal/bulk-send-summary-modal.component'
import { DesktopInvitationCollectionTableComponent } from './components/desktop-invitation-collection-table/desktop-invitation-collection-table.component'
import { InvitaitonTrackerMobileCardComponent } from './components/invitaiton-tracker-mobile-card/invitaiton-tracker-mobile-card.component'
import { InvitationChannelMobileModalComponent } from './components/invitation-channel-mobile-modal/invitation-channel-mobile-modal.component'
import { InvitationSourceDetailsComponent } from './components/invitation-source-details/invitation-source-details.component'
import { InvitationTrackerLoadingComponent } from './components/invitation-tracker-loading/invitation-tracker-loading.component'
import { InvitationTrackerStatisticsComponent } from './components/invitation-tracker-statistics/invitation-tracker-statistics.component'
import { InvitationTrackerPersonaTabsComponent } from './components/persona-tabs/persona-tabs.component'
import { SelectInvitationChannelModalComponent } from './components/select-invitation-channel-modal/select-invitation-channel-modal.component'
import { DeliveryStatusModalComponent } from './delivery-status-modal/delivery-status-modal.component'
import { InvitationTemplatesModalComponent, InvitationTemplatesModalMode } from './invitation-templates-modal/invitation-templates-modal.component'

export type { InvitationTrackerPersona } from '../../types/invitation-tracker.type'

const SOURCE_PANEL_TIMING = '250ms cubic-bezier(0.4, 0, 0.2, 1)'
const MOBILE_LAYOUT_QUERY = '(max-width: 1023px)'

const MOBILE_ACTIONS_DROPDOWN_OPTIONS: DropdownOption[] = [
  {
    key: 'add',
    value: 'Add invitees',
    prefixIcon: `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.2222 6.22222H7.77778V0.777778C7.77778 0.571498 7.69583 0.373667 7.54997 0.227806C7.40411 0.0819442 7.20628 0 7 0C6.79372 0 6.59589 0.0819442 6.45003 0.227806C6.30417 0.373667 6.22222 0.571498 6.22222 0.777778V6.22222H0.777778C0.571498 6.22222 0.373667 6.30417 0.227806 6.45003C0.0819442 6.59589 0 6.79372 0 7C0 7.20628 0.0819442 7.40411 0.227806 7.54997C0.373667 7.69583 0.571498 7.77778 0.777778 7.77778H6.22222V13.2222C6.22222 13.4285 6.30417 13.6263 6.45003 13.7722C6.59589 13.9181 6.79372 14 7 14C7.20628 14 7.40411 13.9181 7.54997 13.7722C7.69583 13.6263 7.77778 13.4285 7.77778 13.2222V7.77778H13.2222C13.4285 7.77778 13.6263 7.69583 13.7722 7.54997C13.9181 7.40411 14 7.20628 14 7C14 6.79372 13.9181 6.59589 13.7722 6.45003C13.6263 6.30417 13.4285 6.22222 13.2222 6.22222Z" fill="#1C64F2"/></svg>`
  }
]

@Component({
  selector: 'app-invitation-tracker',
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    DropdownComponent,
    FilterButtonComponent,
    FilterDropdownTriggerComponent,
    FilterOptionComponent,
    MobileAccordionComponent,
    MobileFilterDrawerComponent,
    SearchFieldComponent,
    ProgressCircleComponent,
    DeleteModalComponent,
    BulkDeleteModalComponent,
    DeliveryStatusModalComponent,
    InvitationTemplatesModalComponent,
    TruncationTooltipComponent,
    ImportModalComponent,
    InvitationTrackerLoadingComponent,
    InvitationTrackerStatisticsComponent,
    InvitationTrackerPersonaTabsComponent,
    InvitaitonTrackerMobileCardComponent,
    DesktopInvitationCollectionTableComponent,
    InvitationChannelMobileModalComponent,
    InvitationSourceDetailsComponent,
    EventsTimelineEmptyStateComponent,
    EventsTimelineEmptyStateComponent,
    SelectInvitationChannelModalComponent,
    BulkSendConfirmModalComponent,
    BulkSendSummaryModalComponent,
    AddEntriesMethodModalComponent,
    AddFromOtherEventModalComponent
  ],
  providers: [InvitationTrackerService, SelfSettingsService, MobileFilterDrawerService, EventService],
  animations: [
    trigger('sourcePanelSlide', [
      transition(':enter', [
        style({ width: 0, opacity: 0, transform: 'translateX(24px)' }),
        animate(SOURCE_PANEL_TIMING, style({ width: '*', opacity: 1, transform: 'translateX(0)' }))
      ]),
      transition(':leave', [animate(SOURCE_PANEL_TIMING, style({ width: 0, opacity: 0, transform: 'translateX(24px)' }))])
    ]),
    trigger('sourceDrawer', [transition(':enter, :leave', [query('@sourceDrawerBackdrop, @sourceDrawerSlide', animateChild())])]),
    trigger('sourceDrawerBackdrop', [
      transition(':enter', [style({ opacity: 0 }), animate(SOURCE_PANEL_TIMING, style({ opacity: 1 }))]),
      transition(':leave', [animate(SOURCE_PANEL_TIMING, style({ opacity: 0 }))])
    ]),
    trigger('sourceDrawerSlide', [
      transition(':enter', [style({ transform: 'translateX(100%)' }), animate(SOURCE_PANEL_TIMING, style({ transform: 'translateX(0)' }))]),
      transition(':leave', [animate(SOURCE_PANEL_TIMING, style({ transform: 'translateX(100%)' }))])
    ])
  ],
  templateUrl: './invitation-tracker.component.html',
  styleUrl: './invitation-tracker.component.scss'
})
export class InvitationTrackerComponent {
  readonly eventId = input.required<number>()
  readonly eventName = input<string>('')
  readonly eventSlug = input<string>('')
  readonly initialPersonaSlug = input<string>('')
  readonly isEventFinished = input<boolean>(false)
  readonly emailTemplateEvent = input<InvitationEmailTemplate | null>(null)
  readonly emailTemplateTimings = input<InvitationEmailTemplateTiming[]>([])

  public readonly deliveryStatusModalId = 'delivery-status-modal'
  public readonly invitationTemplatesModalId = 'invitation-templates-modal'
  public readonly importModalId = 'import-data-modal'
  public readonly addEntriesMethodModalId = 'add-entries-method-modal'
  public readonly addFromOtherEventModalId = 'add-from-other-event-modal'
  public readonly otherEvents = signal<Event[]>([])
  public readonly otherEventsLoading = signal(false)
  public readonly otherEventPersonas = signal<Persona[]>([])
  public readonly otherEventPersonasLoading = signal(false)
  public readonly addFromOtherEventSubmitting = signal(false)
  public readonly removeCollectionItemModalId = 'invitation-collection-remove-modal'
  public readonly bulkRemoveCollectionModalId = 'invitation-collection-bulk-remove-modal'
  public readonly selectInvitationChannelModalId = 'select-invitation-channel-modal'
  public readonly bulkSendSummaryModalId = 'bulk-send-summary-modal'
  public readonly bulkSendConfirmModalId = 'bulk-send-confirm-modal'
  public readonly bulkSendConfirm = signal<InvitationCollectionBulkSendConfirmState | null>(null)
  public readonly invitationChannelMobileModalId = 'invitation-channel-mobile-modal'
  public readonly invitationChannelMobileModalCollectionItemId = signal<number | null>(null)
  public readonly invitationChannelMobileModalChannel = signal<InvitationChannel | null>(null)
  public readonly channelHistory = signal<InvitationChannelHistoryState | null>(null)
  public readonly isSendingInvitation = signal(false)
  public readonly isResolvingSendTemplate = signal(false)
  public readonly isResolvingBulkSendTemplate = signal(false)
  public readonly isInvitationSendBusy = computed(() => this.isSendingInvitation() || this.isResolvingSendTemplate())
  public readonly invitationTemplatesModalMode = signal<InvitationTemplatesModalMode>('save')
  public readonly invitationTemplatesSendFlowChannel = signal<InvitationTemplateChannel>('email')
  public readonly personas = signal<InvitationTrackerPersona[]>([])
  public readonly selectedPersonaId = signal<number | null>(null)
  public readonly personasLoading = signal(false)
  public readonly collectionItems = signal<InvitationCollectionItem[]>([])
  public readonly collectionLoading = signal(false)
  public readonly hasLoadedCollection = signal(false)
  public readonly collectionPage = signal(1)
  public readonly collectionTotal = signal<number | undefined>(undefined)
  public readonly statistics = signal<InvitationCollectionStatistics | null>(null)
  public readonly statisticsLoading = signal(false)
  public readonly isPaginating = signal(false)
  public readonly sortState = signal<GenericTableSort>({ field: null, direction: GenericTableSortDirection.DESC })
  public readonly perPage = 50
  public readonly virtualScrollMinBufferPx = 300
  public readonly virtualScrollMaxBufferPx = 600
  public readonly collectionSelection = signal<InvitationCollectionSelectionState>(createEmptyInvitationCollectionSelection())
  public readonly selectedCollectionKeys = computed(() => getInvitationCollectionSelectedKeys(this.collectionSelection(), this.collectionItems()))
  public readonly selectedCollectionCount = computed(() => getInvitationCollectionSelectionCount(this.collectionSelection(), this.collectionTotal()))
  public readonly hasSelectedCollectionItems = computed(() => this.selectedCollectionCount() > 0)
  public readonly isAllCollectionSelected = computed(() => isAllInvitationCollectionSelected(this.collectionSelection()))
  public readonly canShowBulkRemoveButton = computed(() => canShowInvitationCollectionBulkRemove(this.collectionSelection(), this.collectionTotal(), this.collectionItems()))
  public readonly canShowBulkSendInvitation = computed(() => this.hasSelectedCollectionItems() && !this.isEventFinished())
  public readonly selectedCollectionItemsSummary = computed(() => `(${this.selectedCollectionCount().toLocaleString()}) selected`)
  public readonly bulkRemoveExecuting = signal(false)
  public readonly bulkSendExecuting = signal(false)
  public readonly bulkSendSummary = signal<InvitationCollectionSummary | null>(null)
  public readonly isBulkSendBusy = computed(() => this.bulkSendExecuting() || this.isResolvingBulkSendTemplate())
  public readonly bulkRemoveModalBusy = computed(() => this._bulkRemovePreviewLoading() || this.bulkRemoveExecuting())
  public readonly bulkRemovePreview = signal<InvitationCollectionDeleteItemsPreviewData | null>(null)
  public readonly hasCollectionItems = computed(() => this.collectionItems().length > 0)
  public readonly hasActiveSearch = computed(() => !!this.searchTerm().trim())
  public readonly hasActiveQuery = computed(() => this.hasActiveSearch() || this.activeFilterCount() > 0)
  public readonly hasMore = computed(() => {
    const total = this.collectionTotal()
    if (total == null) return false
    return this.collectionItems().length < total
  })

  public readonly selectedPersona = computed(() => {
    return this.personas().find((persona) => persona.id === this.selectedPersonaId())
  })

  public readonly invitationChannelMobileModalItem = computed(() => {
    const itemId = this.invitationChannelMobileModalCollectionItemId()
    if (itemId == null) return null
    return this.collectionItems().find((item) => item.id === itemId) ?? null
  })

  public readonly invitationChannelMobileModalLatestStatus = computed(() => {
    const item = this.invitationChannelMobileModalItem()
    const channel = this.invitationChannelMobileModalChannel()
    if (!item || !channel) return null
    return getInvitationChannelLatestStatus(item, channel)
  })

  public readonly invitationTemplatesPersonaName = computed(() => {
    return this.selectedPersona()?.name?.trim().toUpperCase() ?? ''
  })

  public readonly invitationTemplatesButtonLabel = computed(() => {
    const personaName = this.invitationTemplatesPersonaName()
    return personaName ? `Customize ${personaName} Template` : 'Customize Template'
  })

  public readonly invitationEmailTemplateInfo = signal<InvitationEmailTemplateInfo>(
    InvitationEmailTemplateModel.create({
      message: ''
    })
  )

  public readonly invitationEmailTemplateEvent = signal<InvitationEmailTemplate>({
    name: '',
    slug: ''
  })

  public readonly invitationEmailTemplateTimings = computed(() => this._fetchedEventTimings() ?? this.emailTemplateTimings())
  public readonly invitationTemplateSaving = signal(false)
  public readonly invitationEmailTemplateLoading = signal(false)
  public readonly invitationTemplateSendingTest = signal(false)
  public readonly invitationWhatsAppTemplateId = signal<WhatsAppInviteTemplateId>(WhatsAppInviteTemplateModel.DEFAULT_TEMPLATE_ID)
  public readonly invitationTemplateTestReceiverEmail = toSignal(inject(AuthService).userChange.pipe(map((user): string => user?.email?.trim() ?? '')), {
    requireSync: true
  })
  public readonly invitationTemplateTestReceiverPhone = toSignal(inject(AuthService).userChange.pipe(map((user): string => user?.phone_number?.trim() ?? '')), {
    requireSync: true
  })
  public readonly organizationId = computed(() => this._authService.currentWorkspace?.id ?? null)
  public readonly importTargetColumns = computed(() => this._buildImportTargetColumns(this.selectedPersona()))
  public readonly mobileActionsDropdownOptions = MOBILE_ACTIONS_DROPDOWN_OPTIONS
  public readonly mobileLoadingCards = [1, 2, 3, 4]
  public readonly isMobileActionsDropdownOpen = signal(false)
  public readonly searchTerm = signal('')
  public readonly appliedFilters = signal<InvitationCollectionFilters>(InvitationCollectionFilterModel.createDefault())
  public readonly activeFilterCount = computed(() => InvitationCollectionFilterModel.countActive(this.appliedFilters()))
  public readonly hasActiveFilters = computed(() => this.activeFilterCount() > 0)
  public readonly sourceEvents = signal<InvitationCollectionSourceEvent[]>([])
  public readonly sourceFilterOptions = computed<ReadonlyArray<InvitationCollectionFilterOption<InvitationCollectionSourceFilter>>>(() =>
    InvitationCollectionFilterModel.buildSourceOptions(this.sourceEvents())
  )
  public readonly filterDropdowns = computed(() => InvitationCollectionFilterModel.buildDropdowns(this.appliedFilters(), this.sourceFilterOptions()))
  public readonly selectedStatisticCardKey = computed(() => InvitationCollectionFilterModel.getMatchingCardKey(this.appliedFilters()))
  public readonly openFilterDropdownKey = signal<InvitationCollectionFilterKey | null>(null)
  public readonly draftFilters = signal<InvitationCollectionFilters>(InvitationCollectionFilterModel.createDefault())
  public readonly filterSections = computed(() => InvitationCollectionFilterModel.buildSections(this.draftFilters(), this.sourceFilterOptions()))
  public readonly pendingRemovalItem = signal<InvitationCollectionItem | null>(null)
  public readonly isMobileLayout = toSignal(
    inject(BreakpointObserver)
      .observe(MOBILE_LAYOUT_QUERY)
      .pipe(map((result) => result.matches)),
    {
      initialValue: typeof window !== 'undefined' && window.matchMedia(MOBILE_LAYOUT_QUERY).matches
    }
  )
  public readonly selectedSourceItem = signal<InvitationCollectionItem | null>(null)
  public readonly isSourcePanelOpen = computed(() => !!this.selectedSourceItem() && !this.isMobileLayout())
  public readonly selectedSourceRowKey = computed(() => {
    const item = this.selectedSourceItem()
    return item ? this.collectionRowKeyFn(item) : null
  })
  public readonly selectedItemSource = signal<InvitationCollectionItemSource | null>(null)
  public readonly itemSourceLoading = signal(false)
  public readonly sourcePanelMaxHeightPx = signal<number | null>(null)
  public readonly removeCollectionItemConfirmMessage = computed(() => {
    const item = this.pendingRemovalItem()
    const personaName = this.selectedPersona()?.name ?? ''

    if (!item) return ''

    return `Are you sure you want to remove<br><strong>${item.fullname ? item.fullname : 'this entry'}</strong> from the <strong>${personaName}</strong> invitation collection?`
  })

  private readonly _modalService = inject(ModalService)
  private readonly _selfSettingsService = inject(SelfSettingsService)
  private readonly _invitationTrackerService = inject(InvitationTrackerService)
  private readonly _eventService = inject(EventService)
  private readonly _fetchedEventTimings = signal<InvitationEmailTemplateTiming[] | null>(null)
  private readonly _inviteService = inject(InviteService)
  private readonly _alertService = inject(AlertService)
  private readonly _navbarService = inject(NavbarService)
  private readonly _authService = inject(AuthService)
  private readonly _ngxTippyService = inject(NgxTippyService)
  private readonly _filterDrawerService = inject(MobileFilterDrawerService)
  private readonly _queuedJobsService = inject(QueuedJobsService)
  private readonly _destroyRef: DestroyRef = inject(DestroyRef)
  private readonly _renderer = inject(Renderer2)
  private readonly _elementRef: ElementRef<HTMLElement> = inject(ElementRef)
  private _didLockBodyScroll = false
  private _currentPage = 1
  private _collectionLoadSubscription?: Subscription
  private _collectionRequestId = 0
  private _statisticsSubscription?: Subscription
  private readonly _collectionContext = computed(() => this._resolveInvitationCollectionContext(), {
    equal: (previous, current) => InvitationTrackerComponent._isSameCollectionContext(previous, current)
  })
  private _bulkRemovePreviewSubscription?: Subscription
  private _bulkRemovePreviewLoading = signal(false)
  private _bulkRemoveExecuteSubscription?: Subscription
  private _otherEventsSubscription?: Subscription
  private _otherEventPersonasSubscription?: Subscription
  private _addFromOtherEventSubscription?: Subscription
  private _bulkSendSubscription?: Subscription
  private _selectionBeforeBulkSend: InvitationCollectionSelectionState | null = null
  private _pendingBulkRemoveSelection: InvitationCollectionDeleteItemsSelection | null = null
  private _channelTemplatesLoadSubscription?: Subscription
  private _channelTemplatesSaveSubscription?: Subscription
  private _inviteTestSubscription?: Subscription
  private _sendInvitationSubscription?: Subscription
  private _sendInvitationHistorySubscription?: Subscription
  private _sendInvitationHistoryPageSubscription?: Subscription
  private _itemSourceSubscription?: Subscription
  private _pendingBulkRemovePayload: InvitationCollectionDeleteItemsRequest | null = null
  private readonly _pendingSendAfterTemplateSaveCollectionItemId = signal<number | null>(null)
  private readonly _pendingBulkSendAfterTemplateSave = signal<InvitationCollectionSendBulkRequest | null>(null)
  private static readonly _CHANNEL_HISTORY_PER_PAGE = 5
  private _hasPendingJobRefresh = false
  /** Max parallel page requests when soft-refreshing loaded pages (API caps per_page at 50). */
  private static readonly _SOFT_RELOAD_CONCURRENCY = 3
  private static readonly _JOB_FINISHED_REFRESH_DEBOUNCE_MS = 500
  private static readonly _ADD_FROM_OTHER_EVENT_FAILED_MESSAGE = 'Could not add entries from the selected event. Please try again.'
  private static readonly _HISTORY_CHANNELS: ReadonlySet<InvitationChannel> = new Set<InvitationChannel>(['email', 'whatsapp'])

  constructor() {
    effect(() => {
      const isSourceDrawerOpen = !!this.selectedSourceItem() && this.isMobileLayout()
      untracked(() => (isSourceDrawerOpen ? this._lockBodyScroll() : this._unlockBodyScroll()))
    })
    this._destroyRef.onDestroy(() => this._unlockBodyScroll())

    this._queuedJobsService.jobsFinished$
      .pipe(debounceTime(InvitationTrackerComponent._JOB_FINISHED_REFRESH_DEBOUNCE_MS), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._refreshAfterJobFinished())

    effect(() => {
      if (!this.isMobileLayout()) untracked(() => this._filterDrawerService.close())
    })

    effect((onCleanup) => {
      if (!this.isSourcePanelOpen()) return

      untracked(() => onCleanup(this._observeSourcePanelMaxHeight()))
    })

    effect((onCleanup) => {
      const workspaceId = this._authService.currentWorkspace?.id
      if (workspaceId == null || !this.eventId()) return

      this.personasLoading.set(true)
      const subscription = this._selfSettingsService
        .getSelfSettings(workspaceId, this.eventId(), { flag: PersonaSettingsFlag.REGULAR_PERSONA })
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (res) => {
            const personas = res.data.map((persona) => ({
              id: persona.id as number,
              name: persona.name as string,
              slug: persona.slug as string,
              formElements: pickPersonaFormElements(persona.formElements)
            }))
            this.personas.set(personas)
            if (personas.length > 0) {
              const preferred = this.initialPersonaSlug() ? personas.find((persona) => persona.slug === this.initialPersonaSlug()) : undefined
              this.selectedPersonaId.set(preferred?.id ?? personas[0].id)
            }
            this.personasLoading.set(false)
          },
          error: () => {
            this.personas.set([])
            this.selectedPersonaId.set(null)
            this.personasLoading.set(false)
          }
        })

      onCleanup(() => subscription.unsubscribe())
    })

    effect((onCleanup) => {
      const workspaceId = this._authService.currentWorkspace?.id
      const eventId = this.eventId()
      if (workspaceId == null || !eventId || this.isEventFinished()) return

      const subscription = this._eventService
        .getEventTimings(workspaceId, eventId)
        .pipe(
          map((res) => (res.data?.timings ? InvitationEmailTemplateModel.mapEventTimings(res.data.timings) : null)),
          catchError(() => of(null)),
          takeUntilDestroyed(this._destroyRef)
        )
        .subscribe((timings) => this._fetchedEventTimings.set(timings))

      onCleanup(() => subscription.unsubscribe())
    })

    effect((onCleanup) => {
      const hasContext = !!this._collectionContext()
      untracked(() => this.hasLoadedCollection.set(false))

      if (!hasContext) {
        this.collectionItems.set([])
        this.collectionTotal.set(undefined)
        this.collectionLoading.set(false)
        untracked(() => this.sourceEvents.set([]))
        return
      }

      // Avoid tracking searchTerm/sortState from _loadPage so search/sort don't re-trigger this effect.
      const subscription: Subscription = untracked(() => this._loadFirstPage())
      onCleanup(() => subscription.unsubscribe())
    })

    effect((onCleanup) => {
      const hasContext = !!this._collectionContext()

      untracked(() => {
        this._statisticsSubscription?.unsubscribe()
        this.statistics.set(null)
        this.statisticsLoading.set(false)
        if (hasContext) this._loadStatistics(true)
      })
      onCleanup(() => this._statisticsSubscription?.unsubscribe())
    })

    this._destroyRef.onDestroy(() => {
      this._channelTemplatesLoadSubscription?.unsubscribe()
      this._channelTemplatesSaveSubscription?.unsubscribe()
      this._sendInvitationSubscription?.unsubscribe()
      this._bulkSendSubscription?.unsubscribe()
      this.isSendingInvitation.set(false)
      this.isResolvingSendTemplate.set(false)
      this.isResolvingBulkSendTemplate.set(false)
      this.bulkSendExecuting.set(false)
      this.bulkSendSummary.set(null)
      this._pendingBulkSendAfterTemplateSave.set(null)
      this.bulkSendConfirm.set(null)
      this.invitationTemplateSaving.set(false)
      this.invitationEmailTemplateLoading.set(false)
    })
  }

  /**
   * Handles import submission from the shared import modal.
   * @param payload {File} - The file to import.
   * @param importModal {ImportModalComponent} - The import modal component.
   * @returns void
   */
  public onImportRequested(payload: { file: File; dataMapping: Record<string, string> }, importModal: ImportModalComponent): void {
    const persona = this.selectedPersona()
    const organizationSlug = this._authService.currentWorkspace?.slug

    if (!persona?.slug || !organizationSlug || !this.eventSlug()) {
      importModal.failImport()
      const missingSlugs: string[] = []
      if (!persona?.slug) missingSlugs.push('persona slug')
      if (!organizationSlug) missingSlugs.push('organization slug')
      if (!this.eventSlug()) missingSlugs.push('event slug')
      this._alertService.alert(Alert.ERROR, `Missing ${missingSlugs.join(', ')}. Please try again.`)
      return
    }

    const dataMapping = buildInvitationCollectionDataMapping(payload.dataMapping)
    const importContext = { organizationSlug, eventSlug: this.eventSlug(), personaSlug: persona.slug }

    this._invitationTrackerService
      .importInvitees(importContext, { file: payload.file, dataMapping })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          importModal.completeImport()
          importModal.close()
          this._alertService.alert(Alert.SUCCESS, response.message || 'Import started')
        },
        error: (error) => {
          importModal.failImport()
          this._alertApiError(error, 'Import failed')
        }
      })
  }

  /**
   * Ensures import state is cleared when the modal closes.
   * @param importModal {ImportModalComponent} - The import modal component.
   * @returns void
   */
  public onImportModalClosed(importModal?: ImportModalComponent): void {
    importModal?.failImport()
  }

  /**
   * Checks if the delivery status modal is open.
   * @returns Boolean indicating if the modal is open.
   */
  public isDeliveryStatusModalOpen(): boolean {
    return this._modalService.isModalOpen(this.deliveryStatusModalId)
  }

  /**
   * Opens the delivery status modal.
   */
  public openDeliveryStatusModal(): void {
    if (!this._modalService.isModalOpen(this.deliveryStatusModalId)) this._modalService.toggleModal(this.deliveryStatusModalId)
  }

  /**
   * Checks if the invitation templates modal is open.
   * @returns Boolean indicating if the modal is open.
   */
  public isInvitationTemplatesModalOpen(): boolean {
    return this._modalService.isModalOpen(this.invitationTemplatesModalId)
  }

  /**
   * Checks if the invitation channel mobile modal is open.
   */
  public isInvitationChannelMobileModalOpen(): boolean {
    return this._modalService.isModalOpen(this.invitationChannelMobileModalId)
  }

  /**
   * Opens the invitation channel mobile modal for a collection entry + channel.
   * @param request - Collection item id and channel (email / whatsapp / sms).
   */
  public openInvitationChannelMobileModal(request: { collectionItemId: number; channel: InvitationChannel }): void {
    this.invitationChannelMobileModalCollectionItemId.set(request.collectionItemId)
    this.invitationChannelMobileModalChannel.set(request.channel)
    this._loadSendInvitationHistory(request)
    if (!this._modalService.isModalOpen(this.invitationChannelMobileModalId)) {
      this._modalService.toggleModal(this.invitationChannelMobileModalId)
    }
  }

  /**
   * Clears modal state after the invitation channel mobile modal closes.
   */
  public onInvitationChannelMobileModalClosed(): void {
    this.invitationChannelMobileModalCollectionItemId.set(null)
    this.invitationChannelMobileModalChannel.set(null)
    this._clearSendInvitationHistory()
  }

  /**
   * Loads send-invitation history when a desktop channel popover opens.
   */
  public onInvitationChannelPanelOpened(request: InvitationChannelSendRequest): void {
    this._loadSendInvitationHistory(request)
  }

  /**
   * Appends the next page of email / WhatsApp send-invitation history when the list is scrolled.
   */
  public onLoadMoreChannelHistory(): void {
    const history = this.channelHistory()
    if (!history || !history.hasMore || history.isLoading || history.isLoadingMore) return
    if (!this._supportsChannelHistory(history.channel)) return

    this.channelHistory.update((current) => (current ? { ...current, isLoadingMore: true } : current))
    this._fetchSendInvitationHistoryPage(history.collectionItemId, history.page + 1, true)
  }

  /**
   * Sends an individual invitation from a channel panel.
   * EMAIL / WHATSAPP send when the channel has a saved template, otherwise open that channel's template step.
   * Finished events cannot send invitations.
   * @param request - Collection item id and channel.
   */
  public onSendInvitationFromChannel(request: InvitationChannelSendRequest): void {
    if (this.isEventFinished()) return
    if (request.channel !== 'email' && request.channel !== 'whatsapp') return
    if (this.isInvitationSendBusy() || this.invitationTemplateSaving()) return

    const channel: InvitationTemplateChannel = request.channel
    const context = this._resolveInvitationCollectionContext()
    if (!context) {
      this._alertService.alert(Alert.ERROR, 'Missing organization, event, or persona. Please try again.')
      return
    }

    this.isResolvingSendTemplate.set(true)
    this._channelTemplatesLoadSubscription?.unsubscribe()
    this._channelTemplatesLoadSubscription = this._fetchChannelTemplates(context)
      .pipe(finalize(() => this.isResolvingSendTemplate.set(false)))
      .subscribe({
        next: ({ email, whatsapp }) => {
          const savedTemplate = channel === 'whatsapp' ? whatsapp : email
          if (savedTemplate) {
            this._sendChannelInvitation(request.collectionItemId, channel)
            return
          }

          this._openInvitationTemplatesModalForSendFlow(context, channel, { collectionItemId: request.collectionItemId })
        },
        error: (error) => {
          this._alertApiError(error, 'Failed to load email template')
          this._openInvitationTemplatesModalForSendFlow(context, channel, { collectionItemId: request.collectionItemId })
        }
      })
  }

  /**
   * Opens the invitation templates modal immediately and loads the last saved EMAIL and WHATSAPP templates in the background.
   */
  public openInvitationTemplatesModal(): void {
    if (this._modalService.isModalOpen(this.invitationTemplatesModalId)) return

    const context = this._resolveInvitationCollectionContext()
    if (!context) {
      this._alertService.alert(Alert.ERROR, 'Missing organization, event, or persona. Please try again.')
      return
    }

    this.invitationTemplatesModalMode.set('save')
    this._pendingSendAfterTemplateSaveCollectionItemId.set(null)
    this._pendingBulkSendAfterTemplateSave.set(null)
    this._prepareInvitationEmailTemplateEvent()
    this.invitationEmailTemplateLoading.set(true)
    this._openInvitationTemplatesModalIfClosed()

    this._channelTemplatesLoadSubscription?.unsubscribe()
    this._channelTemplatesLoadSubscription = this._fetchChannelTemplates(context).subscribe({
      next: ({ email, whatsapp }) => {
        this._applyEmailChannelTemplate(email?.template_data, context)
        this._applyWhatsAppChannelTemplate(whatsapp?.template_data?.email_template)
        this.invitationEmailTemplateLoading.set(false)
      },
      error: (error) => {
        this._alertApiError(error, 'Failed to load email template')
        this._applyEmailChannelTemplate(undefined, context)
        this._applyWhatsAppChannelTemplate(undefined)
        this.invitationEmailTemplateLoading.set(false)
      }
    })
  }

  /**
   * Saves the default WhatsApp template (Save as default on the WhatsApp step).
   * @param templateId - Selected WhatsApp template id (1–3).
   */
  public onInvitationWhatsAppTemplateSave(templateId: WhatsAppInviteTemplateId): void {
    const shouldSendAfterSave = this.invitationTemplatesModalMode() === 'send-flow'
    const pendingSendCollectionItemId = this._pendingSendAfterTemplateSaveCollectionItemId()
    const pendingBulkSend = this._pendingBulkSendAfterTemplateSave()

    this._saveChannelTemplate(
      { channel: 'WHATSAPP', template_data: { email_template: templateId } },
      (response, context) => {
        this._applyWhatsAppChannelTemplate(ChannelTemplateModel.find(response.data, 'WHATSAPP')?.template_data?.email_template ?? templateId)
        this._pendingSendAfterTemplateSaveCollectionItemId.set(null)
        this._pendingBulkSendAfterTemplateSave.set(null)

        if (shouldSendAfterSave && pendingBulkSend) {
          this._openBulkSendConfirmModal(context, pendingBulkSend, 'whatsapp')
          return
        }

        if (shouldSendAfterSave && pendingSendCollectionItemId != null) {
          this._sendChannelInvitation(pendingSendCollectionItemId, 'whatsapp')
          return
        }

        this._alertService.alert(Alert.SUCCESS, response.message || 'WhatsApp template saved')
      },
      'Failed to save WhatsApp template'
    )
  }

  /**
   * Handles Save as default / Save and send from the invitation templates modal.
   * In `send-flow` (no saved template), PATCHes the template then sends the pending invitation.
   * @param invitationInfo - Flushed custom template invitation info.
   */
  public onInvitationEmailTemplateSave(invitationInfo: InvitationEmailTemplateInfo): void {
    const templateData = ChannelTemplateModel.toEmailTemplateData(invitationInfo)
    const shouldSendAfterSave = this.invitationTemplatesModalMode() === 'send-flow'
    const pendingSendCollectionItemId = this._pendingSendAfterTemplateSaveCollectionItemId()
    const pendingBulkSend = this._pendingBulkSendAfterTemplateSave()

    this._saveChannelTemplate(
      { channel: 'EMAIL', template_data: templateData },
      (response, context) => {
        this._applyEmailChannelTemplate(ChannelTemplateModel.find(response.data, 'EMAIL')?.template_data ?? templateData, context)
        this._pendingSendAfterTemplateSaveCollectionItemId.set(null)
        this._pendingBulkSendAfterTemplateSave.set(null)

        if (shouldSendAfterSave && pendingBulkSend) {
          this._openBulkSendConfirmModal(context, pendingBulkSend, 'email')
          return
        }

        if (shouldSendAfterSave && pendingSendCollectionItemId != null) {
          this._sendChannelInvitation(pendingSendCollectionItemId, 'email')
          return
        }

        this._alertService.alert(Alert.SUCCESS, response.message || 'Email template saved')
      },
      'Failed to save email template'
    )
  }

  /**
   * Sends a single test invitation email to the logged-in user (does not mark invitees as sent).
   * Reuses InviteService.inviteTest — same invite-test API as the legacy invite flow.
   */
  public onInvitationEmailTemplateSendTest(invitationInfo: InvitationEmailTemplateInfo): void {
    const receiver = this.invitationTemplateTestReceiverEmail()
    const context = this._resolveInvitationTemplateTestContext(receiver, 'Your account has no email address.')
    if (!context) return

    const inviteInfo = buildInvitationInfo({
      eventSlug: invitationInfo.eventSlug || context.eventSlug,
      formSlug: invitationInfo.formSlug || context.personaSlug,
      fromEventSlug: context.eventSlug,
      fromFormSlug: context.personaSlug,
      message: invitationInfo.message ?? '',
      selectedTemplate: 0,
      action: 'test',
      customButtons: invitationInfo.customButtons,
      imageUrl: invitationInfo.imageUrl,
      cardBackgroundColor: invitationInfo.cardBackgroundColor,
      calendarButtonColor: invitationInfo.calendarButtonColor,
      fontFamily: invitationInfo.fontFamily,
      inviteChannel: NotificationChannel.EMAIL,
      subject: invitationInfo.subject
    })

    this._sendInvitationTemplateTest(inviteInfo, receiver, 'Failed to send test email')
  }

  /**
   * Sends a single test WhatsApp invitation to the logged-in user's phone using the selected template.
   * Reuses InviteService.inviteTest with `send_method: whatsapp`.
   * @param templateId - Selected WhatsApp template id (1–3).
   */
  public onInvitationWhatsAppTemplateSendTest(templateId: WhatsAppInviteTemplateId): void {
    const receiver = this.invitationTemplateTestReceiverPhone()
    const context = this._resolveInvitationTemplateTestContext(receiver, 'Add a phone number in your profile to send a test.')
    if (!context) return

    const inviteInfo = buildInvitationInfo({
      eventSlug: context.eventSlug,
      formSlug: context.personaSlug,
      fromEventSlug: context.eventSlug,
      fromFormSlug: context.personaSlug,
      selectedTemplate: templateId,
      action: 'test',
      inviteChannel: NotificationChannel.WHATSAPP
    })

    this._sendInvitationTemplateTest(inviteInfo, receiver, 'Failed to send WhatsApp test')
  }

  /**
   * Resets templates modal mode when dismissed.
   */
  public onInvitationTemplatesModalClosed(): void {
    if (this._pendingBulkSendAfterTemplateSave()) {
      this._restoreSelectionBeforeBulkSend()
    }
    this.invitationTemplatesModalMode.set('save')
    this.invitationTemplatesSendFlowChannel.set('email')
    this._pendingSendAfterTemplateSaveCollectionItemId.set(null)
    this._pendingBulkSendAfterTemplateSave.set(null)
  }

  /**
   * Checks if the import modal is open.
   * @returns Boolean indicating if the modal is open.
   */
  public isImportModalOpen(): boolean {
    return this._modalService.isModalOpen(this.importModalId)
  }

  /**
   * Opens the import modal for the active persona.
   * @returns void
   */
  public openImportModal(): void {
    this._openModal(this.importModalId)
  }

  /**
   * Checks if the add-entries method chooser modal is open.
   * @returns Boolean indicating if the modal is open.
   */
  public isAddEntriesMethodModalOpen(): boolean {
    return this._modalService.isModalOpen(this.addEntriesMethodModalId)
  }

  /**
   * Opens the method chooser (from other event / import from a file) for the active persona.
   * @returns void
   */
  public openAddEntriesMethodModal(): void {
    if (this.isEventFinished()) return
    this._openModal(this.addEntriesMethodModalId)
  }

  /**
   * Closes the method chooser and continues with the picked adding method.
   * @param method {AddEntriesMethod} - The picked method.
   * @returns void
   */
  public onAddEntriesMethodSelected(method: AddEntriesMethod): void {
    this._closeModal(this.addEntriesMethodModalId)
    if (method === 'file') this.openImportModal()
    else this._openAddFromOtherEventModal()
  }

  /**
   * Checks if the add-from-other-event modal is open.
   * @returns Boolean indicating if the modal is open.
   */
  public isAddFromOtherEventModalOpen(): boolean {
    return this._modalService.isModalOpen(this.addFromOtherEventModalId)
  }

  /**
   * Clears personas of the previously picked source event and loads the new event's personas.
   * @param event {Event | null} - Newly picked source event.
   * @returns void
   */
  public onAddFromOtherEventChanged(event: Event | null): void {
    this._loadOtherEventPersonas(event?.id ?? null)
  }

  /**
   * Adds all registrants of the picked source persona into the current persona's collection,
   * then shows the added / skipped summary and reloads the collection when entries were added.
   * Errors keep the picker open so the user can retry.
   * @param selection {AddFromOtherEventSelection} - Picked source event and persona.
   * @returns void
   */
  public onAddFromOtherEventConfirmed({ event, persona }: AddFromOtherEventSelection): void {
    const target = this._resolveInvitationCollectionContext()
    if (!target || !event.slug || !persona.slug || this.addFromOtherEventSubmitting()) return

    this._addFromOtherEventSubscription?.unsubscribe()
    this.addFromOtherEventSubmitting.set(true)
    this._addFromOtherEventSubscription = this._invitationTrackerService
      .addToInvitationCollection(
        { organizationSlug: target.organizationSlug, eventSlug: event.slug, personaSlug: persona.slug },
        { action: 'all', ids: [], target_event: target.eventSlug, target_persona: target.personaSlug }
      )
      .pipe(
        map((response) => withDefaultAddResult(response)),
        finalize(() => this.addFromOtherEventSubmitting.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (result) => {
          if (!result) {
            this._alertService.alert(Alert.ERROR, InvitationTrackerComponent._ADD_FROM_OTHER_EVENT_FAILED_MESSAGE)
            return
          }
          this._closeModal(this.addFromOtherEventModalId)
          this._openSummaryModal({ kind: 'add', result })
          if (getAddedEntriesCount(result) > 0) this.collectionSelection.set(createEmptyInvitationCollectionSelection())
        },
        error: (error) => this._alertApiError(error, InvitationTrackerComponent._ADD_FROM_OTHER_EVENT_FAILED_MESSAGE)
      })
  }

  /**
   * Handles mobile actions dropdown option selection.
   * @param option {DropdownOption} - The selected dropdown option.
   */
  public onMobileActionsOptionClick(option: DropdownOption): void {
    if (option.key === 'add') this.openAddEntriesMethodModal()
  }

  /**
   * Downloads the CSV import template for the active persona.
   * @returns void
   */
  public downloadImportTemplate(): void {
    const workspaceSlug = this._authService.currentWorkspace?.slug
    if (!this.selectedPersona()?.slug || !workspaceSlug || !this.eventSlug()) return

    window.open(`${environment.registrationsApi}import/${workspaceSlug}/${this.eventSlug()}/${this.selectedPersona()?.slug}`, '_blank')
  }

  /**
   * Selects a persona tab.
   * Keeps the current search term and filters so the new persona collection is queried the same way; only the source filter resets because source options are persona-specific.
   * @param personaId {number} - The ID of the persona to select.
   */
  public selectPersona(personaId: number): void {
    this._clearItemSource()
    this.selectedPersonaId.set(personaId)
    this.collectionSelection.set(createEmptyInvitationCollectionSelection())
    this.sortState.set({ field: null, direction: GenericTableSortDirection.DESC })
    this.appliedFilters.set({ ...this.appliedFilters(), source: [] })
    this.sourceEvents.set([])
  }

  /**
   * Handle search input and reload the collection from page 1.
   * Triggered after debounce (min 3 chars / clear) or immediate Enter (any length).
   * @param search {string} - The search term.
   */
  public onSearchChange(search: string): void {
    const nextSearch = search.trim()
    const previousSearch = this.searchTerm().trim()

    if (nextSearch === previousSearch) return

    this.searchTerm.set(nextSearch)
    this._loadFirstPage()
  }

  /**
   * Toggle the option clicked in a desktop filter dropdown and apply it immediately.
   * @param key {InvitationCollectionFilterKey} - The filter group.
   * @param option {DropdownOption} - The dropdown option.
   */
  public onFilterDropdownSelect(key: InvitationCollectionFilterKey, option: DropdownOption): void {
    if (option.key === undefined) return

    this._applyFilters(InvitationCollectionFilterModel.withToggledValue(this.appliedFilters(), key, option.key, this.sourceFilterOptions()))
  }

  /**
   * Clear the open filter dropdown when it is the one that closed.
   * @param key {InvitationCollectionFilterKey} - The filter group.
   */
  public onFilterDropdownClosed(key: InvitationCollectionFilterKey): void {
    if (this.openFilterDropdownKey() === key) this.openFilterDropdownKey.set(null)
  }

  /**
   * Reset every desktop filter dropdown to its default and reload.
   */
  public onClearAllFilters(): void {
    this._applyFilters(InvitationCollectionFilterModel.createDefault())
  }

  /**
   * Apply the clicked insights card's filter on its own, or clear it when the card is already selected.
   * @param cardKey {InvitationTrackerFilterCardKey} - The clicked card.
   */
  public onStatisticCardClick(cardKey: InvitationTrackerFilterCardKey): void {
    this._applyFilters(InvitationCollectionFilterModel.withCardFilter(this.appliedFilters(), cardKey))
  }

  /**
   * Seed the mobile drawer draft from the applied filters when the drawer opens.
   */
  public onFilterDrawerOpen(): void {
    this.draftFilters.set({ ...this.appliedFilters() })
  }

  /**
   * Toggle an option in a draft filter group from the mobile drawer.
   * @param key {InvitationCollectionFilterKey} - The filter group.
   * @param value {string | number} - The toggled option value.
   */
  public onDrawerFilterSelect(key: InvitationCollectionFilterKey, value: string | number): void {
    this.draftFilters.set(InvitationCollectionFilterModel.withToggledValue(this.draftFilters(), key, value, this.sourceFilterOptions()))
  }

  /**
   * Reset the mobile drawer draft to the default filters.
   */
  public onFilterReset(): void {
    this.draftFilters.set(InvitationCollectionFilterModel.createDefault())
  }

  /**
   * Commit the mobile drawer draft and close the drawer.
   */
  public onFilterApply(): void {
    this._applyFilters({ ...this.draftFilters() })
    this._filterDrawerService.close()
  }

  /**
   * Build a stable row key for selection and tracking.
   */
  public readonly collectionRowKeyFn = (item: InvitationCollectionItem): string => String(item.id)

  /**
   * Track collection items in the list.
   */
  public readonly trackByCollectionItemId = (_index: number, item: InvitationCollectionItem): number => item.id

  /**
   * Update sort state and reload the first page.
   * @param sort {GenericTableSort} - The sort state.
   */
  public onSortChange(sort: GenericTableSort): void {
    this.sortState.set(sort)
    this._loadFirstPage()
  }

  /**
   * Load the next page of collection items.
   */
  public loadNextPage(): void {
    if (!this.hasMore() || this.isPaginating() || this.collectionLoading()) return
    this._loadPage(this._currentPage + 1, true)
  }

  /**
   * Sync selected collection items from the generic table selection output.
   * @param change {GenericTableSelectionChange<InvitationCollectionItem>} - The selection change.
   */
  public onCollectionSelectionChange(change: GenericTableSelectionChange<InvitationCollectionItem>): void {
    this.collectionSelection.set(
      reconcileInvitationCollectionSelectionFromLoadedKeys(this.collectionSelection(), new Set(change.keys), this.collectionItems(), this.collectionTotal())
    )
  }

  /**
   * Selects or clears the entire collection from the desktop table header.
   * @param change {GenericTableSelectAllCollectionChange} - Whether the full collection is selected.
   */
  public onSelectAllCollectionChange(change: GenericTableSelectAllCollectionChange): void {
    this.collectionSelection.set(setInvitationCollectionSelectAll(change.selectAll))
  }

  /**
   * Check whether a collection item is selected.
   * @param item {InvitationCollectionItem} - The collection item.
   */
  public isCollectionItemSelected(item: InvitationCollectionItem): boolean {
    return isInvitationCollectionItemSelected(this.collectionSelection(), item.id)
  }

  /**
   * Toggle selection for a collection item.
   * @param item {InvitationCollectionItem} - The collection item.
   */
  public toggleCollectionItemSelection(item: InvitationCollectionItem): void {
    this.collectionSelection.update((current) => toggleInvitationCollectionItemSelection(current, item.id, this.collectionTotal()))
  }

  /**
   * Opens the select-channel modal before bulk send (Path A: all; Path B: current selection).
   * With no selection, checks every row so the user sees the send targets the whole collection.
   */
  public onBulkSendInvitation(): void {
    if (this.isEventFinished() || this.isBulkSendBusy()) return
    if (!this.hasCollectionItems() && !this.hasActiveQuery()) return

    if (!this._resolveInvitationCollectionContext()) {
      this._alertService.alert(Alert.ERROR, 'Missing organization, event, or persona. Please try again.')
      return
    }

    this._selectionBeforeBulkSend = this.collectionSelection()
    if (!this.hasSelectedCollectionItems()) {
      this.collectionSelection.set(setInvitationCollectionSelectAll(true))
    }

    if (!this._modalService.isModalOpen(this.selectInvitationChannelModalId)) {
      this._modalService.toggleModal(this.selectInvitationChannelModalId)
    }
  }

  /**
   * Restores the pre-send selection when the channel modal is dismissed without picking a channel.
   */
  public onSelectInvitationChannelModalClosed(): void {
    this._restoreSelectionBeforeBulkSend()
  }

  /**
   * Continues bulk send after the user picks a channel in the channel modal.
   * With a saved template for that channel, opens the cost confirmation modal; otherwise opens that channel's template step (send-flow) first.
   * @param channel - UI channel id from the modal.
   */
  public onBulkSendChannelSelected(channel: InvitationChannel): void {
    this._closeSelectInvitationChannelModalIfOpen()

    if (this.isEventFinished() || this.isBulkSendBusy() || this.invitationTemplateSaving() || (channel !== 'email' && channel !== 'whatsapp')) {
      this._restoreSelectionBeforeBulkSend()
      return
    }

    const context = this._resolveInvitationCollectionContext()
    if (!context) {
      this._restoreSelectionBeforeBulkSend()
      this._alertService.alert(Alert.ERROR, 'Missing organization, event, or persona. Please try again.')
      return
    }

    const hadSelection = this.hasSelectedCollectionItems()
    const selection = hadSelection ? buildInvitationCollectionDeleteItemsSelectionPayload(this.collectionSelection()) : { all: true }
    const filters = buildInvitationCollectionDeleteItemsFilters(this.searchTerm(), InvitationCollectionFilterModel.toQueryFilters(this.appliedFilters()))
    const apiChannel = INVITATION_CHANNEL_TO_API[channel]
    const payload: InvitationCollectionSendBulkRequest = filters ? { selection, filters, channel: apiChannel } : { selection, channel: apiChannel }

    const preview = this._buildKnownBulkSendPreview(channel)
    if (preview?.invited_count === 0) {
      this._restoreSelectionBeforeBulkSend()
      this._openSummaryModal({ kind: 'send', source: 'before-send', data: preview })
      return
    }

    this.isResolvingBulkSendTemplate.set(true)
    this._channelTemplatesLoadSubscription?.unsubscribe()
    this._channelTemplatesLoadSubscription = this._fetchChannelTemplates(context)
      .pipe(finalize(() => this.isResolvingBulkSendTemplate.set(false)))
      .subscribe({
        next: ({ email, whatsapp }) => {
          const savedTemplate = channel === InvitationTemplatesStep.WHATSAPP ? whatsapp : email
          if (savedTemplate) {
            this._openBulkSendConfirmModal(context, payload, channel)
            return
          }

          this._openInvitationTemplatesModalForSendFlow(context, channel, { bulkPayload: payload })
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? `Failed to load ${INVITATION_CHANNEL_TITLE[channel]} template`)
          this._openInvitationTemplatesModalForSendFlow(context, channel, { bulkPayload: payload })
        }
      })
  }

  /**
   * Checks whether the select-channel modal is open.
   */
  public isSelectInvitationChannelModalOpen(): boolean {
    return this._modalService.isModalOpen(this.selectInvitationChannelModalId)
  }

  /**
   * Checks whether the bulk send confirmation modal is open.
   */
  public isBulkSendConfirmModalOpen(): boolean {
    return this._modalService.isModalOpen(this.bulkSendConfirmModalId)
  }

  /**
   * Sends the pending bulk invitations after the user confirms the cost; the modal stays open until the API responds.
   */
  public onBulkSendConfirmed(): void {
    if (!this.bulkSendConfirm()) return
    this._executeBulkSend(this.bulkSendConfirm()!.context, this.bulkSendConfirm()!.payload)
  }

  /**
   * Drops the pending bulk send and returns to the select-channel modal, keeping the pre-send selection snapshot.
   */
  public onBulkSendConfirmBack(): void {
    if (this.bulkSendExecuting() || !this.bulkSendConfirm()) return
    this._closeBulkSendConfirmModal()
    if (!this._modalService.isModalOpen(this.selectInvitationChannelModalId)) {
      this._modalService.toggleModal(this.selectInvitationChannelModalId)
    }
  }

  /**
   * Drops the pending bulk send and restores the pre-send selection (X / backdrop / Escape).
   * Ignored while the send is in flight — the response closes the modal and opens the summary.
   */
  public onBulkSendConfirmCancelled(): void {
    if (this.bulkSendExecuting() || !this.bulkSendConfirm()) return
    this._closeBulkSendConfirmModal()
    this._restoreSelectionBeforeBulkSend()
  }

  /**
   * Checks whether the bulk-send summary modal is open.
   */
  public isBulkSendSummaryModalOpen(): boolean {
    return this._modalService.isModalOpen(this.bulkSendSummaryModalId)
  }

  /**
   * Clears summary state when the post-send modal is dismissed.
   */
  public onBulkSendSummaryModalClosed(): void {
    this.bulkSendSummary.set(null)
  }

  /**
   * Opens the bulk remove preview modal immediately, then loads counts from the API.
   * When every selected entry is already invited, shows an error alert instead.
   */
  public onBulkRemoveSelected(): void {
    const organizationSlug = this._authService.currentWorkspace?.slug
    const personaSlug = this.selectedPersona()?.slug

    if (!organizationSlug || !this.eventSlug() || !personaSlug || this.bulkRemoveExecuting()) return

    if (areAllSelectedInvitationCollectionItemsNonRemovable(this.collectionSelection(), this.collectionTotal(), this.collectionItems())) {
      this._alertService.alert(Alert.ERROR, "Selected entries can't be removed. They've already been invited.")
      return
    }

    this._bulkRemovePreviewSubscription?.unsubscribe()
    this.bulkRemovePreview.set(null)
    this._bulkRemovePreviewLoading.set(true)

    if (!this._modalService.isModalOpen(this.bulkRemoveCollectionModalId)) {
      this._modalService.toggleModal(this.bulkRemoveCollectionModalId)
    }

    const selection = buildInvitationCollectionDeleteItemsSelectionPayload(this.collectionSelection())
    const filters = buildInvitationCollectionDeleteItemsFilters(this.searchTerm(), InvitationCollectionFilterModel.toQueryFilters(this.appliedFilters()))
    const payload: InvitationCollectionDeleteItemsRequest = filters ? { selection, filters } : { selection }
    this._pendingBulkRemovePayload = payload

    this._bulkRemovePreviewSubscription = this._invitationTrackerService
      .previewDeleteCollectionItems({ organizationSlug, eventSlug: this.eventSlug(), personaSlug }, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this._bulkRemovePreviewLoading.set(false)

          if (!response.data?.will_be_removed) {
            this._alertService.alert(Alert.ERROR, "Selected entries can't be removed. They've already been invited.")
            this.cancelBulkRemoveCollection()
            return
          }

          this.bulkRemovePreview.set(response.data)
        },
        error: (error) => {
          this._bulkRemovePreviewLoading.set(false)
          this._alertApiError(error, 'Failed to preview bulk remove')
          this.cancelBulkRemoveCollection()
        }
      })
  }

  /**
   * Confirms bulk remove via the delete-bulk API, then reloads the collection.
   */
  public confirmBulkRemoveCollection(): void {
    const organizationSlug = this._authService.currentWorkspace?.slug
    const personaSlug = this.selectedPersona()?.slug
    const payload = this._pendingBulkRemovePayload

    if (!organizationSlug || !this.eventSlug() || !personaSlug || !payload || this.bulkRemoveExecuting()) return

    this._bulkRemoveExecuteSubscription?.unsubscribe()
    this.bulkRemoveExecuting.set(true)

    this._bulkRemoveExecuteSubscription = this._invitationTrackerService
      .deleteCollectionItemsBulk({ organizationSlug, eventSlug: this.eventSlug(), personaSlug }, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.bulkRemoveExecuting.set(false)
          this._pendingBulkRemovePayload = null
          this.collectionSelection.set(createEmptyInvitationCollectionSelection())
          this._closeBulkRemoveCollectionModal()
          this._alertService.alert(Alert.SUCCESS, response.message || 'Invitees removed successfully')
          this._loadFirstPage()
        },
        error: (error) => {
          this.bulkRemoveExecuting.set(false)
          this._alertApiError(error, 'Failed to remove invitees')
        }
      })
  }

  /**
   * Cancels bulk remove and closes the preview modal.
   */
  public cancelBulkRemoveCollection(): void {
    this._bulkRemovePreviewSubscription?.unsubscribe()
    this._bulkRemovePreviewLoading.set(false)
    if (!this.bulkRemoveExecuting()) this._pendingBulkRemovePayload = null
    this._closeBulkRemoveCollectionModal()
  }

  /**
   * Checks whether the bulk remove preview modal is open.
   */
  public isBulkRemoveCollectionModalOpen(): boolean {
    return this._modalService.isModalOpen(this.bulkRemoveCollectionModalId)
  }

  /**
   * Clears preview state and closes the bulk remove modal if it is open.
   */
  private _closeBulkRemoveCollectionModal(): void {
    this.bulkRemovePreview.set(null)

    if (this._modalService.isModalOpen(this.bulkRemoveCollectionModalId)) {
      this._modalService.toggleModal(this.bulkRemoveCollectionModalId)
    }
  }

  /**
   * Opens the remove confirmation modal for a collection item.
   * @param item {InvitationCollectionItem} - The collection item to remove.
   */
  public onRemoveCollectionItem(item: InvitationCollectionItem): void {
    this.pendingRemovalItem.set(item)

    if (!this._modalService.isModalOpen(this.removeCollectionItemModalId)) {
      this._modalService.toggleModal(this.removeCollectionItemModalId)
    }
  }

  /**
   * Confirms removal of the pending collection item via the delete API.
   */
  public confirmRemoveCollectionItem(): void {
    const item = this.pendingRemovalItem()
    const organizationSlug = this._authService.currentWorkspace?.slug
    const personaSlug = this.selectedPersona()?.slug

    if (!item || !organizationSlug || !this.eventSlug() || !personaSlug) return

    this._invitationTrackerService
      .deleteCollectionItem({ organizationSlug, eventSlug: this.eventSlug(), personaSlug }, item.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this._removeCollectionItemFromLocalState(item.id)
          this._alertService.alert(Alert.SUCCESS, response.message || 'Invitee removed successfully')
          this.pendingRemovalItem.set(null)
        },
        error: (error) => {
          this._alertApiError(error, 'Failed to remove invitee')
        }
      })
  }

  /**
   * Loads the source data (original fields + source event/persona) for the clicked collection item.
   * @param item {InvitationCollectionItem} - The collection item whose source was clicked.
   */
  public onCollectionSourceClick(item: InvitationCollectionItem): void {
    const context = this._resolveInvitationCollectionContext()
    if (!context) return

    this._clearItemSource()
    this.selectedSourceItem.set(item)
    this.itemSourceLoading.set(true)

    this._itemSourceSubscription = this._invitationTrackerService
      .getInvitationCollectionItemSource(context, item.id)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.itemSourceLoading.set(false))
      )
      .subscribe({
        next: (source) => this.selectedItemSource.set(source),
        error: (error) => this._alertApiError(error, 'Failed to load source data')
      })
  }

  /**
   * Closes the data source panel and clears its state.
   */
  public closeSourceDetails(): void {
    this._clearItemSource()
  }

  /**
   * Cancels removal and closes the confirmation modal.
   */
  public cancelRemoveCollectionItem(): void {
    this.pendingRemovalItem.set(null)

    if (this._modalService.isModalOpen(this.removeCollectionItemModalId)) {
      this._modalService.toggleModal(this.removeCollectionItemModalId)
    }
  }

  /**
   * Checks whether the remove confirmation modal is open.
   */
  public isRemoveCollectionItemModalOpen(): boolean {
    return this._modalService.isModalOpen(this.removeCollectionItemModalId)
  }

  /**
   * Removes a collection item from local state after a successful delete.
   * @param itemId {number} - The collection item ID.
   */
  private _removeCollectionItemFromLocalState(itemId: number): void {
    this.collectionItems.update((items) => items.filter((entry) => entry.id !== itemId))
    this.collectionSelection.update((current) => removeInvitationCollectionItemFromSelection(current, itemId))
    this.collectionTotal.update((total) => (total == null ? total : Math.max(total - 1, 0)))
  }

  /**
   * Builds the import target columns for the active persona.
   * @param persona {InvitationTrackerPersona | undefined} - The persona to build the import target columns for.
   * @returns The import target columns.
   */
  private _buildImportTargetColumns(persona: InvitationTrackerPersona | undefined): ImportTargetColumn[] {
    if (!persona?.formElements.length) return []

    return buildImportTargetColumnsFromFormElements(persona.formElements)
  }

  /**
   * Resolves org/event/persona slugs for invitation-collection APIs.
   */
  private _resolveInvitationCollectionContext(): InvitationCollectionContext | null {
    const organizationSlug = this._authService.currentWorkspace?.slug
    const eventSlug = this.eventSlug()
    const personaSlug = this.selectedPersona()?.slug
    if (!organizationSlug || !eventSlug || !personaSlug) return null

    return { organizationSlug, eventSlug, personaSlug }
  }

  /**
   * Guards a template send-test: footer not busy, receiver present, and collection context resolved.
   * Alerts and returns null when the test cannot be sent.
   * @param receiver - Trimmed email or phone of the logged-in user.
   * @param missingReceiverDetail - Alert detail shown when the receiver is empty.
   */
  private _resolveInvitationTemplateTestContext(receiver: string, missingReceiverDetail: string): InvitationCollectionContext | null {
    if (this.invitationTemplateSendingTest() || this.invitationTemplateSaving()) return null

    if (!receiver) {
      this._alertService.alert(Alert.ERROR, `Cannot send test. ${missingReceiverDetail}`)
      return null
    }

    const context = this._resolveInvitationCollectionContext()
    if (!context) {
      this._alertService.alert(Alert.ERROR, 'Missing organization, event, or persona. Please try again.')
      return null
    }

    return context
  }

  /**
   * Sends a single test invitation via invite-test and toggles the templates modal busy state.
   * @param inviteInfo - Test payload (channel, template, content).
   * @param receiver - Email or phone receiving the test.
   * @param failureMessage - Fallback alert when the API returns no message.
   */
  private _sendInvitationTemplateTest(inviteInfo: InvitationInfo, receiver: string, failureMessage: string): void {
    this.invitationTemplateSendingTest.set(true)
    this._inviteTestSubscription?.unsubscribe()
    this._inviteTestSubscription = this._inviteService
      .inviteTest(inviteInfo, receiver)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.invitationTemplateSendingTest.set(false))
      )
      .subscribe({
        next: () => {
          this._alertService.alert(Alert.SUCCESS, `Test has been sent to ${receiver}`)
        },
        error: (error) => {
          this._alertApiError(error, failureMessage)
        }
      })
  }

  /**
   * Hydrates the email template editor state from channel-template API data.
   */
  private _applyEmailChannelTemplate(templateData: InvitationEmailChannelTemplateData | null | undefined, context: InvitationCollectionContext): void {
    this.invitationEmailTemplateInfo.set(
      ChannelTemplateModel.toInvitationEmailTemplateInfo(templateData, {
        eventSlug: context.eventSlug,
        formSlug: context.personaSlug
      })
    )
  }

  /**
   * Opens the invitation templates modal when it is currently closed.
   */
  private _openInvitationTemplatesModalIfClosed(): void {
    if (!this._modalService.isModalOpen(this.invitationTemplatesModalId)) {
      this._modalService.toggleModal(this.invitationTemplatesModalId)
    }
  }

  /**
   * Closes the invitation templates modal when it is open.
   */
  private _closeInvitationTemplatesModal(): void {
    this._channelTemplatesLoadSubscription?.unsubscribe()
    this.invitationEmailTemplateLoading.set(false)
    if (this._modalService.isModalOpen(this.invitationTemplatesModalId)) {
      this._modalService.toggleModal(this.invitationTemplatesModalId)
    }
  }

  /**
   * Opens the templates modal on the channel's template step when send was clicked with no saved template.
   * After the user saves, the pending single or bulk invitation is sent automatically.
   * @param context - Organization, event, and persona slugs.
   * @param channel - Template step to open (email / whatsapp).
   * @param options - Pending single collection item id and/or bulk send payload.
   */
  private _openInvitationTemplatesModalForSendFlow(
    context: InvitationCollectionContext,
    channel: InvitationTemplateChannel,
    options: { collectionItemId?: number; bulkPayload?: InvitationCollectionSendBulkRequest }
  ): void {
    this._hideInvitationChannelPopovers()
    this._closeInvitationChannelMobileModalIfOpen()
    this.invitationTemplatesModalMode.set('send-flow')
    this.invitationTemplatesSendFlowChannel.set(channel)
    this._pendingSendAfterTemplateSaveCollectionItemId.set(options.collectionItemId ?? null)
    this._pendingBulkSendAfterTemplateSave.set(options.bulkPayload ?? null)
    if (channel === 'whatsapp') {
      this._applyWhatsAppChannelTemplate(undefined)
    } else {
      this._prepareInvitationEmailTemplateEvent()
      this._applyEmailChannelTemplate(undefined, context)
    }
    this._openInvitationTemplatesModalIfClosed()
  }

  /**
   * POSTs bulk invitations; closes the confirm modal on settle and opens the summary modal on success.
   * @param context - Organization, event, and persona slugs.
   * @param payload - Bulk send request (selection, optional filters, channel).
   */
  private _executeBulkSend(context: InvitationCollectionContext, payload: InvitationCollectionSendBulkRequest): void {
    if (this.bulkSendExecuting()) return

    this._bulkSendSubscription?.unsubscribe()
    this.bulkSendExecuting.set(true)

    this._bulkSendSubscription = this._invitationTrackerService
      .sendInvitationBulk(context, payload)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.bulkSendExecuting.set(false))
      )
      .subscribe({
        next: (response) => {
          this._closeBulkSendConfirmModal()
          this._selectionBeforeBulkSend = null
          this.collectionSelection.set(createEmptyInvitationCollectionSelection())
          if (response.data) this._openSummaryModal({ kind: 'send', source: 'after-send', data: response.data })
        },
        error: (error) => {
          this._closeBulkSendConfirmModal()
          this._restoreSelectionBeforeBulkSend()
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? 'Failed to send invitations')
        }
      })
  }

  /**
   * Builds the before-send summary from the selected rows when all of them are loaded; null when the selection is not fully known.
   * @param channel - UI channel the invitations would be sent on.
   */
  private _buildKnownBulkSendPreview(channel: InvitationTemplateChannel): InvitationCollectionSendBulkData | null {
    const selectedItems = getKnownSelectedInvitationCollectionItems(this.collectionSelection(), this.collectionTotal(), this.collectionItems())
    return selectedItems?.length ? buildBulkSendSummaryFromItems(selectedItems, channel) : null
  }

  /**
   * Opens the cost confirmation modal before a bulk send; the send runs only after the user confirms.
   * Recipients are the full selection count.
   * With no selected recipients the API is called directly so its summary explains why nothing was sent.
   * @param context - Organization / event / persona slugs for the API.
   * @param payload - Bulk send request (selection, optional filters, channel).
   * @param channel - UI channel the invitations are sent on (drives copy and unit price).
   */
  private _openBulkSendConfirmModal(context: InvitationCollectionContext, payload: InvitationCollectionSendBulkRequest, channel: InvitationTemplateChannel): void {
    const recipientCount = this.selectedCollectionCount()
    if (recipientCount === 0) {
      this._executeBulkSend(context, payload)
      return
    }

    this.bulkSendConfirm.set({
      context,
      payload,
      channel,
      recipientCount,
      unitPriceUsd: invitationChannelUnitPrice(channel, 'invitation', this._authService.invitationUnitPriceUsd, this._authService.resendConfirmationUnitPriceUsd),
      totalCostUsd: this._authService.calculateInvitationTotalPriceUsd(INVITATION_CHANNEL_TO_NOTIFICATION[channel], recipientCount)
    })
    if (!this._modalService.isModalOpen(this.bulkSendConfirmModalId)) {
      this._modalService.toggleModal(this.bulkSendConfirmModalId)
    }
  }

  /**
   * Clears the pending confirmation and closes its modal.
   */
  private _closeBulkSendConfirmModal(): void {
    this.bulkSendConfirm.set(null)
    if (this._modalService.isModalOpen(this.bulkSendConfirmModalId)) {
      this._modalService.toggleModal(this.bulkSendConfirmModalId)
    }
  }

  /**
   * Puts back the selection captured when the bulk-send flow started (no-op if nothing was captured).
   */
  private _restoreSelectionBeforeBulkSend(): void {
    if (!this._selectionBeforeBulkSend) return
    this.collectionSelection.set(this._selectionBeforeBulkSend)
    this._selectionBeforeBulkSend = null
  }

  /**
   * Opens the summary modal with bulk-send counts (before / after send) or add-from-other-event counts.
   * @param summary {InvitationCollectionSummary} - Data shown by the summary modal.
   */
  private _openSummaryModal(summary: InvitationCollectionSummary): void {
    this.bulkSendSummary.set(summary)
    this._openModal(this.bulkSendSummaryModalId)
  }

  /**
   * POSTs an individual invitation for a collection item on the given channel.
   * @param collectionItemId - Invitation collection entry id.
   * @param channel - Delivery channel (email / whatsapp).
   */
  private _sendChannelInvitation(collectionItemId: number, channel: InvitationChannel): void {
    if (this.isSendingInvitation()) return

    const blockedSendState = this._resolveChannelBlockedSendState(collectionItemId, channel)
    if (blockedSendState && !blockedSendState.showSendCta) {
      this._alertService.alert(Alert.ERROR, blockedSendState.message)
      return
    }

    const context = this._resolveInvitationCollectionContext()
    if (!context) {
      this._alertService.alert(Alert.ERROR, 'Missing organization, event, or persona. Please try again.')
      return
    }

    this.isSendingInvitation.set(true)
    this._sendInvitationSubscription = this._invitationTrackerService
      .sendInvitation(context, collectionItemId, { channel: INVITATION_CHANNEL_TO_API[channel] })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          this.isSendingInvitation.set(false)
          this._hideInvitationChannelPopovers()
          this._closeInvitationChannelMobileModalIfOpen()
        })
      )
      .subscribe({
        next: (response) => {
          this._alertService.alert(Alert.SUCCESS, response.message || 'Invitation sent')
          this._refreshTrackerData(this._currentPage, this._currentPage, 'Invitation sent, but the list could not be refreshed', true)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.error ?? error?.error?.message ?? 'Failed to send invitation')
          this._refreshTrackerData(this._currentPage, this._currentPage, 'Invitation sent, but the list could not be refreshed', false)
        }
      })
  }

  /**
   * Resolves the blocked-send state for a collection item + channel using the same rules as the channel panels.
   * Returns null when the item is not on the current page (backend remains the source of truth).
   * @param collectionItemId - Invitation collection entry id.
   * @param channel - Delivery channel.
   */
  private _resolveChannelBlockedSendState(collectionItemId: number, channel: InvitationChannel): InvitationChannelBlockedSendState | null {
    const item = this.collectionItems().find((entry) => entry.id === collectionItemId)
    if (!item) return null
    return resolveBlockedSendStateForItem(item, channel, this.channelHistory(), this.isEventFinished())
  }

  /**
   * Hides open invitation-channel Tippy popovers (desktop) after a successful send.
   */
  private _hideInvitationChannelPopovers(): void {
    this._ngxTippyService.getInstances()?.forEach((instance) => {
      if (instance.props?.theme === 'invitation-channel') {
        instance.hide()
      }
    })
  }

  /**
   * Loads paginated email / WhatsApp send-invitation history for a collection item.
   * Other channels keep the empty panel until their APIs are wired.
   */
  private _loadSendInvitationHistory(request: { collectionItemId: number; channel: InvitationChannel }): void {
    this._sendInvitationHistorySubscription?.unsubscribe()
    this._sendInvitationHistoryPageSubscription?.unsubscribe()

    this.channelHistory.set({
      collectionItemId: request.collectionItemId,
      channel: request.channel,
      entries: [],
      total: 0,
      totalCost: 0,
      page: 1,
      isLoading: this._supportsChannelHistory(request.channel),
      isLoadingMore: false,
      hasMore: false
    })

    if (!this._supportsChannelHistory(request.channel)) return

    this._fetchSendInvitationHistoryPage(request.collectionItemId, 1, false)
  }

  /**
   * Fetches one send-invitation history page and either replaces or appends rows.
   */
  private _fetchSendInvitationHistoryPage(collectionItemId: number, page: number, append: boolean): void {
    const context = this._resolveInvitationCollectionContext()
    if (!context) {
      this.channelHistory.update((current) =>
        current && current.collectionItemId === collectionItemId ? { ...current, isLoading: false, isLoadingMore: false, hasMore: false } : current
      )
      return
    }

    if (!append) {
      this.channelHistory.update((current) => (current && current.collectionItemId === collectionItemId ? { ...current, isLoading: true, isLoadingMore: false } : current))
    }

    const requestChannel = this.channelHistory()?.channel
    if (!requestChannel) return
    const perPage = InvitationTrackerComponent._CHANNEL_HISTORY_PER_PAGE

    const assignSubscription = (sub: Subscription) => {
      if (append) {
        this._sendInvitationHistoryPageSubscription?.unsubscribe()
        this._sendInvitationHistoryPageSubscription = sub
      } else {
        this._sendInvitationHistorySubscription?.unsubscribe()
        this._sendInvitationHistorySubscription = sub
      }
    }

    assignSubscription(
      this._invitationTrackerService
        .getSendInvitationHistory(context, collectionItemId, INVITATION_CHANNEL_TO_API[requestChannel], page, perPage)
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          finalize(() => {
            this.channelHistory.update((current) => {
              if (!current || current.collectionItemId !== collectionItemId || current.channel !== requestChannel) return current
              return append ? { ...current, isLoadingMore: false } : { ...current, isLoading: false }
            })
          })
        )
        .subscribe({
          next: (response) => {
            this.channelHistory.update((current) => {
              if (!current || current.collectionItemId !== collectionItemId || current.channel !== requestChannel) return current
              const entries = response.data ?? []
              const mergedEntries = append ? [...current.entries, ...entries] : entries
              const total = response.total
              return {
                ...current,
                entries: mergedEntries,
                total,
                totalCost: response.total_cost ?? 0,
                page,
                hasMore: mergedEntries.length < total
              }
            })
          },
          error: () => {
            this.channelHistory.update((current) => {
              if (!current || current.collectionItemId !== collectionItemId || current.channel !== requestChannel) return current
              if (append) return current
              return {
                ...current,
                entries: [],
                total: 0,
                totalCost: 0,
                page: 1,
                hasMore: false
              }
            })
          }
        })
    )
  }

  /**
   * Whether send-invitation history is fetched for the channel (SMS keeps the empty panel).
   * @param channel {InvitationChannel} - Channel to check.
   * @returns True when history is loaded from the API.
   */
  private _supportsChannelHistory(channel: InvitationChannel): boolean {
    return InvitationTrackerComponent._HISTORY_CHANNELS.has(channel)
  }

  /**
   * Clears active channel history panel state.
   */
  private _clearSendInvitationHistory(): void {
    this._sendInvitationHistorySubscription?.unsubscribe()
    this._sendInvitationHistoryPageSubscription?.unsubscribe()
    this.channelHistory.set(null)
  }

  /**
   * Cancels any in-flight source request and clears the selected source state.
   */
  private _clearItemSource(): void {
    this._itemSourceSubscription?.unsubscribe()
    this.selectedSourceItem.set(null)
    this.selectedItemSource.set(null)
    this.itemSourceLoading.set(false)
  }

  /**
   * Tracks the visible height of the scroll container so the desktop source panel never exceeds the screen.
   * @returns {() => void} - Teardown that stops observing.
   */
  private _observeSourcePanelMaxHeight(): () => void {
    const scrollContainer = this._elementRef.nativeElement.closest<HTMLElement>('[data-wizard-scroll]')
    const measure = (): void => this.sourcePanelMaxHeightPx.set(scrollContainer?.clientHeight ?? window.innerHeight)

    measure()

    if (!scrollContainer || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(scrollContainer)
    return () => resizeObserver.disconnect()
  }

  /**
   * Locks page scroll behind the mobile source drawer.
   */
  private _lockBodyScroll(): void {
    if (this._didLockBodyScroll) return
    this._renderer.addClass(document.body, 'tw-overflow-hidden')
    this._didLockBodyScroll = true
  }

  /**
   * Releases the page scroll lock only if this component applied it and no modal still needs it.
   */
  private _unlockBodyScroll(): void {
    if (!this._didLockBodyScroll) return
    this._didLockBodyScroll = false
    if (!this._modalService.hasOpenModal()) this._renderer.removeClass(document.body, 'tw-overflow-hidden')
  }

  /**
   * Shows an error alert using the API message when present.
   * @param error {{ error?: { message?: string }; message?: string } | null | undefined} - HTTP or runtime error.
   * @param fallback {string} - Message used when the error has none.
   */
  private _alertApiError(error: { error?: { message?: string; error?: string }; message?: string } | null | undefined, fallback: string): void {
    this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.error?.error ?? error?.message ?? fallback)
  }

  /**
   * Closes the invitation channel mobile modal and clears its state when open.
   */
  private _closeInvitationChannelMobileModalIfOpen(): void {
    if (this._modalService.isModalOpen(this.invitationChannelMobileModalId)) {
      this._modalService.toggleModal(this.invitationChannelMobileModalId)
    }
    this.onInvitationChannelMobileModalClosed()
  }

  /**
   * Closes the bulk select-channel modal when open.
   */
  private _closeSelectInvitationChannelModalIfOpen(): void {
    this._closeModal(this.selectInvitationChannelModalId)
  }

  /**
   * Opens the event / persona picker for adding registrants from another event.
   * @returns void
   */
  private _openAddFromOtherEventModal(): void {
    this._loadOtherEventPersonas(null)
    this._loadOtherEvents()
    this._openModal(this.addFromOtherEventModalId)
  }

  /**
   * Loads organization events for the add-from-other-event picker; cancels any in-flight request.
   * @returns void
   */
  private _loadOtherEvents(): void {
    const organizationId = this.organizationId()
    if (organizationId == null) return

    this._otherEventsSubscription?.unsubscribe()
    this.otherEventsLoading.set(true)
    this._otherEventsSubscription = this._eventService
      .getEventsList({ organizationId, flag: EventsListFlag.REGULAR_EVENT })
      .pipe(
        finalize(() => this.otherEventsLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => this.otherEvents.set(response.data ?? []),
        error: (error) => {
          this.otherEvents.set([])
          this._alertAddFromOtherEventError(error, 'Failed to load events')
        }
      })
  }

  /**
   * Clears the previous source event's personas and loads regular personas (with registration counts)
   * of the picked source event; cancels any in-flight request.
   * @param eventId {number | null} - Picked source event id; `null` only clears.
   * @returns void
   */
  private _loadOtherEventPersonas(eventId: number | null): void {
    this._otherEventPersonasSubscription?.unsubscribe()
    this.otherEventPersonas.set([])

    const organizationId = this.organizationId()
    if (organizationId == null || eventId == null) return

    this.otherEventPersonasLoading.set(true)
    this._otherEventPersonasSubscription = this._selfSettingsService
      .getSelfSettings(organizationId, eventId, { flag: PersonaSettingsFlag.REGULAR_PERSONA, registerCount: true })
      .pipe(
        finalize(() => this.otherEventPersonasLoading.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => this.otherEventPersonas.set(AddFromOtherEventModel.toPersonas(response.data ?? [])),
        error: (error) => {
          this.otherEventPersonas.set([])
          this._alertAddFromOtherEventError(error, 'Failed to load personas')
        }
      })
  }

  /**
   * Shows a load error for the add-from-other-event picker only while it is still open.
   * @param error {{ error?: { message?: string }; message?: string }} - API error.
   * @param fallback {string} - Message used when the API does not provide one.
   * @returns void
   */
  private _alertAddFromOtherEventError(error: { error?: { message?: string }; message?: string }, fallback: string): void {
    if (this.isAddFromOtherEventModalOpen()) this._alertApiError(error, fallback)
  }

  /**
   * Opens a modal by id when it is not already open.
   * @param modalId {string} - Modal id registered with `ModalService`.
   */
  private _openModal(modalId: string): void {
    if (!this._modalService.isModalOpen(modalId)) this._modalService.toggleModal(modalId)
  }

  /**
   * Closes a modal by id when it is open.
   * @param modalId {string} - Modal id registered with `ModalService`.
   */
  private _closeModal(modalId: string): void {
    if (this._modalService.isModalOpen(modalId)) this._modalService.toggleModal(modalId)
  }

  /**
   * Prepares event preview inputs from values passed into the tracker wizard.
   * Timings are loaded by the tracker itself; editor appearance is hydrated separately from GET `channel-templates`.
   */
  private _prepareInvitationEmailTemplateEvent(): void {
    const previewEvent = this.emailTemplateEvent()
    this.invitationEmailTemplateEvent.set({
      name: previewEvent?.name ?? this.eventName(),
      slug: previewEvent?.slug ?? this.eventSlug(),
      public_start_date: previewEvent?.public_start_date ?? null,
      public_end_date: previewEvent?.public_end_date ?? null,
      location_details: previewEvent?.location_details ?? null,
      registration_amount: previewEvent?.registration_amount,
      currency: previewEvent?.currency,
      forms: previewEvent?.forms
    })
  }

  /**
   * Loads the saved EMAIL and WHATSAPP channel template rows for the active persona, if any.
   */
  private _fetchChannelTemplates(context: InvitationCollectionContext): Observable<{ email?: EmailChannelTemplate; whatsapp?: WhatsAppChannelTemplate }> {
    return this._invitationTrackerService.getChannelTemplates(context).pipe(
      map((response) => ({
        email: ChannelTemplateModel.find(response.data, 'EMAIL'),
        whatsapp: ChannelTemplateModel.find(response.data, 'WHATSAPP')
      }))
    )
  }

  /**
   * PATCHes a channel template, closes the templates modal on success, then runs the channel-specific follow-up.
   * @param payload - Channel + `template_data` to persist.
   * @param onSaved - Applies the saved row (and any send / toast) after the modal closes.
   * @param errorFallback - Error toast copy when the API returns no message.
   */
  private _saveChannelTemplate(
    payload: PatchChannelTemplatePayload,
    onSaved: (response: Response<ChannelTemplate[]>, context: InvitationCollectionContext) => void,
    errorFallback: string
  ): void {
    if (this.invitationTemplateSaving() || this.invitationTemplateSendingTest()) return

    const context = this._resolveInvitationCollectionContext()
    if (!context) {
      this._alertService.alert(Alert.ERROR, 'Missing organization, event, or persona. Please try again.')
      return
    }

    this.invitationTemplateSaving.set(true)
    this._channelTemplatesSaveSubscription?.unsubscribe()
    this._channelTemplatesSaveSubscription = this._invitationTrackerService
      .patchChannelTemplate(context, payload)
      .pipe(finalize(() => this.invitationTemplateSaving.set(false)))
      .subscribe({
        next: (response) => {
          this._closeInvitationTemplatesModal()
          onSaved(response, context)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? errorFallback)
        }
      })
  }

  /**
   * Stores the saved WhatsApp template id, falling back to the default template for missing / invalid ids.
   * @param templateId - `template_data.email_template` from the WHATSAPP channel row.
   */
  private _applyWhatsAppChannelTemplate(templateId: number | null | undefined): void {
    this.invitationWhatsAppTemplateId.set(WhatsAppInviteTemplateModel.templateIdOrDefault(templateId))
  }

  /**
   * Fetches one invitation-collection page with the active sort/search.
   */
  private _fetchInvitationCollectionPage(page: number): Observable<InvitationCollectionListResponse> | null {
    const organizationSlug = this._authService.currentWorkspace?.slug
    const personaSlug = this.selectedPersona()?.slug

    if (!organizationSlug || !this.eventSlug() || !personaSlug || page < 1) return null

    const sort = this.sortState()
    const sortParam = sort.field ? { field: sort.field, direction: sort.direction } : undefined
    const search: string | undefined = this.searchTerm().trim() || undefined
    const filters = InvitationCollectionFilterModel.toQueryFilters(this.appliedFilters())

    return this._invitationTrackerService.getInvitationCollection({ organizationSlug, eventSlug: this.eventSlug(), personaSlug }, page, this.perPage, sortParam, search, filters)
  }

  /**
   * Commits the applied filters and reloads page 1 when the resulting API query changes.
   * @param next {InvitationCollectionFilters} - The filters to apply.
   */
  private _applyFilters(next: InvitationCollectionFilters): void {
    const previous = this.appliedFilters()
    this.appliedFilters.set(next)

    if (!InvitationCollectionFilterModel.isSameQuery(previous, next)) this._loadFirstPage()
  }

  /**
   * Reloads the invitation collection from page 1.
   */
  private _loadFirstPage(): Subscription {
    this._currentPage = 1
    return this._loadPage(1, false)
  }

  /**
   * Fetches persona-wide invitation statistics (ignores search/filters); keeps the previous values visible while refreshing.
   * @param isInitialLoad {boolean} - Alerts on failure only for the first load of a persona so post-send refreshes fail silently.
   */
  private _loadStatistics(isInitialLoad = false): void {
    const context = this._collectionContext()
    if (!context) return

    this._statisticsSubscription?.unsubscribe()
    this.statisticsLoading.set(true)

    this._statisticsSubscription = this._invitationTrackerService
      .getInvitationStatistics(context)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.statisticsLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          if (this._isActiveCollectionContext(context)) this.statistics.set(response.data ?? null)
        },
        error: (error) => {
          if (isInitialLoad) this._alertApiError(error, 'Failed to load invitation statistics')
        }
      })
  }

  /**
   * Soft-refreshes collection pages `fromPage..toPage` and, when requested, the wallet balance and persona statistics.
   * @param fromPage - First page to refresh (1-based).
   * @param toPage - Last page to refresh (inclusive).
   * @param errorMessage - Alert shown when the list refresh fails; null refreshes silently.
   * @param refreshCounters - Also refetch the wallet balance and persona statistics.
   */
  private _refreshTrackerData(fromPage: number, toPage: number, errorMessage: string | null, refreshCounters: boolean): void {
    if (refreshCounters) this._navbarService.refreshBalance()
    if (!this._collectionContext()) return

    if (refreshCounters) this._loadStatistics()
    this._softReloadCollectionPages(fromPage, toPage, errorMessage)
  }

  /**
   * Silently refreshes the balance, statistics and loaded pages when a Queued job finishes.
   * The in-progress count is workspace-wide, so any job finishing (any type, any member) triggers it.
   * While a page load is in flight the refresh is deferred until it completes, so the load isn't cancelled.
   */
  private _refreshAfterJobFinished(): void {
    if (this.collectionLoading() || this.isPaginating()) {
      this._hasPendingJobRefresh = true
      return
    }

    this._hasPendingJobRefresh = false
    this._refreshTrackerData(1, this._currentPage, null, true)
  }

  /**
   * Soft-refreshes loaded pages `fromPage..toPage` in place without the loading shimmer.
   * Rows are spliced back at their original offset so earlier pages stay and the list length is kept
   * (a replace-all reload would shrink the list and trigger virtual-scroll loadMore).
   * @param fromPage - First page to refresh (1-based).
   * @param toPage - Last page to refresh (inclusive).
   * @param errorMessage - Alert shown when the refresh fails; null fails silently.
   */
  private _softReloadCollectionPages(fromPage: number, toPage: number, errorMessage: string | null): Subscription {
    const firstPage = Math.max(1, fromPage)
    const lastPage = Math.max(firstPage, toPage)
    const requests: Observable<{ index: number; response: InvitationCollectionListResponse }>[] = []
    for (let page = firstPage; page <= lastPage; page++) {
      const request$ = this._fetchInvitationCollectionPage(page)
      if (!request$) return new Subscription()
      const index = page - firstPage
      requests.push(request$.pipe(map((response) => ({ index, response }))))
    }

    const requestId = this._startCollectionRequest()
    const startIndex = (firstPage - 1) * this.perPage

    this._collectionLoadSubscription = from(requests)
      .pipe(mergeAll(InvitationTrackerComponent._SOFT_RELOAD_CONCURRENCY), toArray(), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (results) => {
          if (requestId !== this._collectionRequestId) return

          const responses = results.sort((a, b) => a.index - b.index).map((result) => result.response)
          const lastResponse = responses[responses.length - 1]
          const merged = [...this.collectionItems()]
          merged.splice(startIndex, requests.length * this.perPage, ...responses.flatMap((response) => response.data))
          this.collectionItems.set(merged)
          this.collectionTotal.set(lastResponse.total)
          this.collectionPage.set(lastResponse.page ?? lastPage)
          this._currentPage = Math.max(this._currentPage, lastResponse.page ?? lastPage)
        },
        error: () => {
          if (requestId !== this._collectionRequestId || !errorMessage) return
          this._alertService.alert(Alert.ERROR, errorMessage)
        }
      })

    return this._collectionLoadSubscription
  }

  /**
   * Cancels the in-flight collection request and returns a new request id for stale-response checks.
   */
  private _startCollectionRequest(): number {
    this._collectionLoadSubscription?.unsubscribe()
    return ++this._collectionRequestId
  }

  /**
   * Returns true when a request's context still matches the active tracker selection.
   */
  private _isActiveCollectionContext(context: InvitationCollectionContext): boolean {
    return InvitationTrackerComponent._isSameCollectionContext(this._resolveInvitationCollectionContext(), context)
  }

  /**
   * Compares two collection contexts by their org/event/persona slugs.
   * @param previous {InvitationCollectionContext | null} - First context.
   * @param current {InvitationCollectionContext | null} - Second context.
   * @returns True when both are null or all slugs match.
   */
  private static _isSameCollectionContext(previous: InvitationCollectionContext | null, current: InvitationCollectionContext | null): boolean {
    if (!previous || !current) return previous === current
    return previous.organizationSlug === current.organizationSlug && previous.eventSlug === current.eventSlug && previous.personaSlug === current.personaSlug
  }

  /**
   * Fetches a page of invitation collection items.
   * @param page {number} - Page number to fetch.
   * @param append {boolean} - Whether to append results to the current list.
   */
  private _loadPage(page: number, append: boolean): Subscription {
    const request$ = this._fetchInvitationCollectionPage(page)
    if (!request$) return new Subscription()

    const requestId = this._startCollectionRequest()

    if (append) this.isPaginating.set(true)
    else this.collectionLoading.set(true)

    this._collectionLoadSubscription = request$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        if (requestId !== this._collectionRequestId) return

        this.collectionItems.set(append ? [...this.collectionItems(), ...response.data] : response.data)
        this.collectionPage.set(response.page ?? page)
        this.collectionTotal.set(response.total)
        this._currentPage = response.page ?? page
        this._setSourceEvents(response.source_data)
        this.hasLoadedCollection.set(true)
        this.collectionLoading.set(false)
        this.isPaginating.set(false)
        if (this._hasPendingJobRefresh) this._refreshAfterJobFinished()
      },
      error: () => {
        if (requestId !== this._collectionRequestId) return

        if (!append) {
          this.collectionItems.set([])
          this.collectionTotal.set(undefined)
        }
        this.collectionLoading.set(false)
        this.isPaginating.set(false)
        if (this._hasPendingJobRefresh) this._refreshAfterJobFinished()
      }
    })

    return this._collectionLoadSubscription
  }

  /**
   * Stores the persona's source events and resets any source filter pointing at an event that is no longer listed.
   * @param sourceData {InvitationCollectionSourceEvent[] | undefined} - The source events from the collection response.
   */
  private _setSourceEvents(sourceData: InvitationCollectionSourceEvent[] | undefined): void {
    if (!sourceData) return

    this.sourceEvents.set(sourceData)
    const sourceOptions = this.sourceFilterOptions()
    this.appliedFilters.set(InvitationCollectionFilterModel.withValidSource(this.appliedFilters(), sourceOptions))
    this.draftFilters.set(InvitationCollectionFilterModel.withValidSource(this.draftFilters(), sourceOptions))
  }
}
